<script setup lang="ts">
  import { onMounted, ref, computed } from 'vue';
  import { ShippingType } from '@othercommerce/domain';
  import { useFormContext, setModelWithContext, getModelFromContext, useTranslator } from '@othercommerce/foundation';

  interface Props {
    service: string;
    types: ShippingType[];
    mapKey: string | null;
    isSandbox: boolean;
  }

  interface PolkurierPoint {
    id: string;
    name?: string;
    address: string;
  }

  const props = defineProps<Props>();

  const form = useFormContext();
  const loading = ref(true);
  const translator = useTranslator();
  const map = ref();

  const shippingPointId = computed(() => {
    return getModelFromContext('shippingPointId', form, null);
  });

  const shippingPointName = computed(() => {
    return getModelFromContext('shippingPointName', form, null);
  });

  const error = computed(() => {
    const pointId = form!.errors.value.shippingPointId || [];
    const pointName = form!.errors.value.shippingPointName || [];

    if (pointId.length > 0) {
      return pointId[0];
    } else if (pointName.length > 0) {
      return pointName[0];
    }

    return undefined;
  });

  function openMap(mapKey: string | null) {
    if (mapKey === null) {
      return alert(
        translator.trans(
          'SHOP.CART.MESSAGE.SHIPPING_ALERT_UNABLE_TO_USE_PICKUP_POINT',
        ),
      );
    }

    map.value = new window.POLKURIER.PointsMap({
      token: mapKey,
      providers: [props.service],
      functions: ['collect'],
    });

    map.value.onConfirm((point: PolkurierPoint) => {
      setModelWithContext('shippingPointId', form, point.id);
      setModelWithContext('shippingPointName', form, point.name || point.address || point.id);

      map.value.closeMapDialog();
    });

    map.value.showMapDialog();
  }

  const POLKURIER_CSS_URL =
    'https://maps.polkurier.pl/assets/dist/points-map.css';
  const POLKURIER_SCRIPT_URL =
    'https://maps.polkurier.pl/assets/dist/points-map.bundle.js';

  onMounted(() => {
    const existingStylesheet =
      document.querySelector<HTMLLinkElement>(
        `link[href="${POLKURIER_CSS_URL}"]`,
      );

    if (!existingStylesheet) {
      document
        .querySelectorAll('link[data-polkurier-map]')
        .forEach((element) => element.remove());

      const stylesheet = document.createElement('link');

      stylesheet.rel = 'stylesheet';
      stylesheet.href = POLKURIER_CSS_URL;
      stylesheet.dataset.polkurierMap = 'true';

      document.head.appendChild(stylesheet);
    }

    if (window.POLKURIER) {
      loading.value = false;
      return;
    }

    const existingScript =
      document.querySelector<HTMLScriptElement>(
        `script[src="${POLKURIER_SCRIPT_URL}"]`,
      );

    if (existingScript) {
      existingScript.addEventListener(
        'load',
        () => {
          loading.value = false;
        },
        { once: true },
      );

      existingScript.addEventListener(
        'error',
        () => {
          loading.value = false;
        },
        { once: true },
      );

      return;
    }

    // Remove a previously failed script
    document
      .querySelectorAll('script[data-polkurier-map]')
      .forEach((element) => element.remove());

    const script = document.createElement('script');

    script.src = POLKURIER_SCRIPT_URL;
    script.async = true;
    script.dataset.polkurierMap = 'true';

    script.onload = () => {
      loading.value = false;
    };

    script.onerror = () => {
      loading.value = false;

      console.error(`Could not load Polkurier map script: ${POLKURIER_SCRIPT_URL}`);
    };

    document.head.appendChild(script);
  });
</script>

<template>
  <div class="polkurier-map-widget">
    <SfDataLoader :loading="loading">
      <div class="polkurier-map-widget__point">
        <div class="polkurier-map-widget__icon">
          <SfIconPointer aria-hidden="true" />
        </div>
        <div class="polkurier-map-widget__details">
          <div v-if="shippingPointId && shippingPointName" class="polkurier-map-widget__name">
            {{ shippingPointId }} {{ shippingPointName }}
          </div>
          <SfButton small @click="openMap(mapKey)" transparent type="button" :loading>
            {{ shippingPointName ? $t('SHOP.CART.ACTION.SHIPPING_CHANGE_PICKUP_POINT') : $t('SHOP.CART.ACTION.SHIPPING_SELECT_PICKUP_POINT') }}
          </SfButton>
        </div>
      </div>
    </SfDataLoader>

    <p v-if="error" class="control__error" v-html="error" />
  </div>
</template>

<style lang="scss">
  .polkurier-map-widget {
    &__point {
      display: flex;
      align-items: center;
      gap: 1rem;
      border: 1px dashed var(--secondary-border);
      padding: 1rem;
      border-radius: var(--border-radius);
    }

    &__icon {
      font-size: 1.5rem;
      line-height: 1;
    }

    &__details {
      display: flex;
      align-items: center;
      line-height: 1.5rem;
      gap: 0.5rem;
    }

    &__name {
      font-weight: var(--font-weight-bold);
    }

    .control__error {
      color: var(--sf-input-error-color);
      font-size: var(--sf-input-error-font-size);
      padding: var(--sf-input-error-padding-y) var(--sf-input-error-padding-x);
      scroll-margin-top: var(--sf-input-error-scroll-margin-top);
      scroll-margin-bottom: var(--sf-input-error-scroll-margin-bottom);
      margin-top: 1rem;
    }
  }
</style>